import { chain, find, get, map, pick, toUpper, omit, findIndex, forEach, size, includes, isObject, isEmpty, reduce, compact, maxBy, filter, slice, flatten, replace, identity, each, first } from "lodash"
import { computePositionSet } from "./securities"
import { percentageFormatter } from "helpers/formatters/numbers"
import { segmentPositionSet } from "helpers/segments"
import { ASSET_TYPES } from "helpers/assets/constants"
import { squashUnmodeledBaskets } from "helpers/swaps"

export const ITEMS_COLORS = ["#03a9f4", "#9067a7", "#009688", "#ffcd00", "#0d47a1"]
export function getBaseComparisonTypes(isOpPro) {
  return [
    {
      id: "performance",
      name: "Performance",
      base: true,
      tooltip: "Ex-post performance with factor attribution",
    },
    {
      id: "risk",
      name: "Predictive Risk",
      base: true,
      disabled: isOpPro,
      tooltip: isOpPro ? "Only available with enterprise plan" : "Ex-ante total risk & factor risk decomposition",
    },
    {
      id: "exposure",
      name: "Exposure",
      base: true,
      tooltip: "Weighted-average factor exposure",
    },
    {
      id: "composition",
      name: "Composition",
      base: true,
    },
    {
      id: "predictedFactorSensitivity",
      name: "Predicted Factor Sensitivity",
      base: true,
      type: "factorSensitivity",
      path: "factor-sensitivity/predicted",
      disabled: isOpPro,
      tooltip: isOpPro ? "Only available with enterprise plan" : undefined,
    },
    {
      id: "historicalFactorSensitivity",
      name: "Historical Factor Sensitivity",
      base: true,
      type: "factorSensitivity",
      path: "factor-sensitivity/historical",
    },
  ]
}

export function computeSecuritiesQueries(comparisonSecurities) {
  return comparisonSecurities
    .map(item =>
      `${item.alias}: security(ticker: "${item.ticker}", mic: "${item.type === "swap" ? "_SWP" : item.mic}") {
          availability {
            from
            to
          }
        }`,
    ).join("")
}

export function computeSecuritiesDescriptorsQueries(comparisonSecurities) {
  return comparisonSecurities
    .map(item =>
      `${item.alias}: security(
        ticker: "${item.ticker}"
        mic: "${item.type === "swap" ? "_SWP" : item.mic}"
        ${item.internalId ? `internalId: "${item.internalId}"` : ""} ) {
          descriptors (on: $on) {
            name
          }
        }`,
    ).join("")
}

export function getCoveragePositionSetQuery(positionSet, startDate, resourceId) {
  if (positionSet.type === "inline") {
    return positionSet.positionSet
  }
  if (positionSet.type === "security" && positionSet.assetClass === "swap") {
    return {
      dates: [{
        date: startDate,
        ...positionSet.positionSet,
      }],
    }
  } else if (positionSet.type === "security") {
    return {
      dates: [{
        date: startDate,
        // We only support searching and adding equities.
        equities: [{
          id: pick(positionSet, ["modelProviderId", "ticker", "mic", "internalId"]),
          economicExposure: 1,
        }],
      }],
    }
  } else if (positionSet.type === "experiment") {
    if (positionSet.parentType === "research_topic") {
      return { id: resourceId, experimentId: positionSet.id, type: "RESEARCH_TOPIC" }
    }
    return { id: resourceId, experimentId: positionSet.id, type: "PORTFOLIO" }
  } else {
    return { id: positionSet.id, type: toUpper(positionSet.type) }
  }
}

export function getComparisonNonOverlappingItems(pnbAvailability, portfolioEarliestDate, comparisonItems, securitiesAvailability, earliestDate, latestDate, experimentsAvailability) {
  return chain(comparisonItems)
    .map(item => {
      let itemEarliestDate
      let itemLatestDate

      switch (item.type) {
        case "security":
        case "swap":
          itemEarliestDate = securitiesAvailability[item.id] ? securitiesAvailability[item.id].from : null
          itemLatestDate = securitiesAvailability[item.id] ? securitiesAvailability[item.id].to : null

          if (itemEarliestDate && ((itemLatestDate < earliestDate) || (itemEarliestDate > latestDate))) {
            return {
              id: item.id,
              label: item.label,
            }
          }
          break
        case "benchmark":
          // enable benchmarks not to be filtered out of compared items when switching risk model
          itemEarliestDate = get(pnbAvailability, [item.id, "availableFrom"])
          if (itemEarliestDate && (itemEarliestDate > latestDate)) {
            return {
              id: item.id,
              label: item.label,
            }
          }
          break
        case "portfolio":
          itemEarliestDate = get(pnbAvailability, [item.id, "availableFrom"])
          if (!itemEarliestDate || (itemEarliestDate > latestDate)) {
            return {
              id: item.id,
              label: item.label,
            }
          }
          break
        case "experiment":
          itemEarliestDate = get(experimentsAvailability, item.id)
          if (!itemEarliestDate || (itemEarliestDate > latestDate)) {
            return {
              id: item.id,
              label: item.label,
            }
          }
          break
      }
      return null
    })
    .filter(item => item)
    .value()
}

export function getItems(comparisonItems, currentPositionSet, securities, { benchmarks, portfolios }, modeledItems, experiments) {
  const items = [{
    id: currentPositionSet.id,
    type: "portfolio",
    label: currentPositionSet.name,
    color: ITEMS_COLORS[0],
    initialItem: true,
    fxHedgeEnabled: currentPositionSet.fxHedgeEnabled,
  },
  ...map(comparisonItems, (item, i) => {
    const itemData = {
      ...item,
      id: item.id,
      type: item.type,
      color: ITEMS_COLORS[i + 1],
      uploading: !!item.uploading,
      isUnmodeled: (!modeledItems || !modeledItems[i]) && !item.uploading,
      evaluateAs: item.evaluateAs || "long",
    }

    let experiment

    switch (item.type) {
      case "security":
        itemData.label = get(securities, `[${item.ticker}${item.mic}]descriptors.name`, item.label)
        itemData.ticker = item.ticker
        itemData.mic = item.mic
        itemData.internalId = item.internalId
        itemData.assetClass = item.assetClass
        itemData.modelProviderId = item.modelProviderId
        break
      case "swap":
        itemData.label = item.label
        itemData.ticker = item.id
        itemData.assetClass = "swap"
        break
      case "benchmark":
        itemData.label = get(find(benchmarks, { id: item.id }), "name", item.label)
        break
      case "experiment":
        experiment = find(experiments, { id: item.id })
        if (experiment) {
          itemData.portfolioId = currentPositionSet.id
          itemData.label = get(experiment, "name", item.label)
          itemData.noData = !get(experiment, "availableFrom") && !itemData.uploading
          itemData.availableFrom = get(experiment, "availableFrom")
          itemData.experimentType = experiment.type
          itemData.description = experiment.description
          itemData.lastUpdated = experiment.lastUpdated
        }

        break
      default:
        itemData.label = get(find(portfolios, { id: item.id }), "name", item.label)
        break
    }
    return itemData
  })]

  return chain(items)
    .map(item => {
      const positionSet = { ...item }
      if (positionSet.type === "security" || (positionSet.type === "swap" && !item.segment)) {
        positionSet.positionSet = computePositionSet(positionSet.assetClass, positionSet.ticker, positionSet.mic, positionSet.modelProviderId, positionSet.evaluateAs, positionSet.internalId)
      }

      return positionSet
    })
    .value()
}

export function getSecurities(positionSets) {
  return chain(positionSets)
    .filter(item => item.type === "security" || item.type === "swap")
    .map(item => ({
      ...item,
      alias: item.id.replace(/-/gi, "_").split(".").join(""),
    }))
    .value()
}

function basePositionSet(positionSets, n, startDate, composition) {
  if (positionSets[n].type === "inline") {
    return { ...positionSets[n].positionSet, fxHedgeEnabled: positionSets[n].fxHedgeEnabled }
  } else if (positionSets[n].type === "security" || (positionSets[n].assetClass === "swap" && !composition && !positionSets[n].segment)) {
    return {
      fxHedgeEnabled: positionSets[n].fxHedgeEnabled,
      dates: [{
        date: startDate,
        ...pick(positionSets[n].positionSet, ASSET_TYPES, "equity"),
      }],
    }
  } else if (positionSets[n].type === "experiment") {
    if (positionSets[n].researchTopicId) {
      return { id: positionSets[n].researchTopicId, experimentId: positionSets[n].id, type: "RESEARCH_TOPIC", fxHedgeEnabled: positionSets[n].fxHedgeEnabled }
    }
    return { id: positionSets[n].portfolioId, experimentId: positionSets[n].id, type: "PORTFOLIO", fxHedgeEnabled: positionSets[n].fxHedgeEnabled }
  } else {
    return { id: positionSets[n].id, type: toUpper(positionSets[n].type), fxHedgeEnabled: positionSets[n].fxHedgeEnabled }
  }
}

export function getPositionSet(positionSets, n, startDate, { composition, currency } = {}) {
  const positionSet = squashUnmodeledBaskets(segmentPositionSet(basePositionSet(positionSets, n, startDate, composition), get(positionSets, [n, "segment"])))

  return {
    ...positionSet,
    currency,
  }
}

export function updateUploadingExperiments(comparisonItems, notifications) {
  return comparisonItems.map(item => {
    if (item.uploading) {
      const notification = find(notifications, notification => notification.data.experimentId === item.id)

      if (notification) {
        const errorCount = get(notification, "error", 0)
        const successCount = get(notification, "success", 0)

        if (notification.total === errorCount + successCount) {
          return omit(item, "uploading")
        }
      } else {
        return omit(item, "uploading")
      }
    }

    return item
  })
}

export function updateAlternativeExperimentProgress(comparisonItems, updatedNotifications) {
  let modified = false
  const newComparisonItems = [...comparisonItems]
  if (size(newComparisonItems) > 0) {
    forEach(updatedNotifications, notification => {
      if (notification.type === "portfolio-doctor" && notification.data.experimentId) {
        const errorCount = get(notification, "error", 0)
        const successCount = get(notification, "success", 0)

        if (notification.total === errorCount + successCount) {
          const comparisonIndex = findIndex(newComparisonItems, { id: notification.data.experimentId })
          if (comparisonIndex > -1) {
            modified = true
            newComparisonItems[comparisonIndex] = omit(newComparisonItems[comparisonIndex], "uploading")
          }
        }
      }
    })
  }

  if (!modified) {
    return null
  }

  return newComparisonItems
}

export const disableSearchResult = item => !includes([
  "security",
  "portfolio",
  "benchmark",
  "index",
  "experiment",
], item.type)

export function getBackwardsCompatibleSelectedType(selectedType, defaultType = "performance") {
  // Default value if no type is selected
  if (isEmpty(selectedType)) { return { id: "performance", type: "performance" } }

  // Backwards compatibility code. Current userprefs would only have the type id saved as a string
  if (!isObject(selectedType)) {
    const selectedId = selectedType || defaultType
    return {
      id: selectedId,
      type: !includes(["summary", "scorecard", "performance", "composition", "exposure", "risk", "exposure", "frontier"], selectedId) ? "contentset" : null,
    }
  } else {
    return {
      id: selectedType.id,
      type: selectedType.type || selectedType.id,
    }
  }
}

export function getComparisonFactorsData(selectedDate, categories, favoriteFactors, factorGetter, data0, data1, data2, data3, data4) {
  const date = find(data0, d => d.date === selectedDate)
  if (!date) {
    return null
  }

  // Get all the other factors for the other items at the selected date, flattened. We'll use that to fill
  // the top X factors per categories if we won't have at least X factors returned on the main item
  const allOtherDatesFactors = chain([data1, data2, data3, data4])
    .map(dataDates => chain(dataDates)
      .find(d => d.date === selectedDate)
      .get(factorGetter)
      .value())
    .compact()
    .flatten()
    .value()

  const allFactors = chain(date)
    .get(factorGetter)
    .map(f => ({ ...f, priorityOrder: 1 }))
    .concat(allOtherDatesFactors)
    .uniqBy(item => `${item.id}${item.category}`)
    .value()

  return chain(categories)
    .filter(category => !category.singleFactor)
    .map(category => ({
      id: category.id,
      factors: chain(allFactors)
        .filter(f => f.category === category.id)
        .map(factor => ({
          label: factor.name,
          id: factor.id,
          value: factor.value,
          priorityOrder: factor.priorityOrder,
          fn: item => percentageFormatter(get(item, ["factors", factor.id], 0)),
          favorite: !!favoriteFactors[factor.id],
        }))
        .value(),
    }))
    .keyBy("id")
    .mapValues("factors")
    .value()
}

export function getComparisonTop5Factors(factorsData) {
  return chain(factorsData)
    .mapValues(factors =>
      chain(factors)
        .filter(f => !f.favorite)
        .orderBy(f => [f.priorityOrder, Math.abs(f.value)], ["desc", "desc"])
        .take(5)
        .value(),
    )
    .value()
}

export function getComparisonFavoriteFactors(factorsData) {
  return chain(factorsData)
    .mapValues(factors =>
      chain(factors)
        .filter(f => f.favorite)
        .orderBy(f => [f.priorityOrder, Math.abs(f.value)], ["desc", "desc"])
        .value(),
    )
    .value()
}

export function getMetricFromComparisonType(comparisonType, compositionMetric, riskMetric, exposureMetric, performanceMetric, experimentComparisonType) {
  const comparisonTypeId = get(comparisonType, "id") || get(experimentComparisonType, "id")
  switch (comparisonTypeId) {
    case "composition":
      return compositionMetric
    case "risk":
      return riskMetric
    case "performance":
      return performanceMetric
    case "exposure":
    default:
      return exposureMetric
  }
}

export const tableDownloadDataReducer = (items, data, sections, subSectionName) => reduce(sections, (summaryResults, section) => {
  const itemsToMap = compact([
    (section.fn || section.getter) && section,
    ...get(section, subSectionName, []),
  ])
  return [
    ...summaryResults,
    ...map(itemsToMap, (sectionRow) => makeComparisonRow(sectionRow, items, data, section.label)),
  ]
}, [])

function makeComparisonRow(row, compareItems, data, parentLabel, skipParentLabel = false) {
  const downloadParentLabel = skipParentLabel ? "" : (parentLabel || row?.label)
  const compareData = compact(data)
  const rowLabel = (!skipParentLabel && downloadParentLabel === row?.label) ? "TOTAL" : row?.label
  const getter = row?.fn || row?.getter
  const cleanedCompareData = cleanCompareDownloadData(compareData, compareItems)
  return [
    downloadParentLabel,
    rowLabel,
    ...map(compareItems, ({ type }, index) => {
      const compareDataItem = cleanedCompareData[index]
      if (compareDataItem == null || !getter) {
        return null
      }
      if (getter.length === 2) {
        return getter(compareDataItem, type)
      }
      return getter(compareDataItem)
    }),
  ]
}

function cleanCompareDownloadData(compareData, compareItems) {
  const compareDataRowSize = size(filter(compareData, identity))
  const compareItemsSize = size(compareItems)
  const cleanedCompareData = [...compareData]
  each(compareItems, (item, i) => {
    const itemIsUnmodeled = get(item, "isUnmodeled")
    if (compareDataRowSize === compareItemsSize && itemIsUnmodeled) {
      cleanedCompareData[i] = null
    } else if (itemIsUnmodeled) {
      cleanedCompareData.splice(i, 0, null)
    }
  })
  return cleanedCompareData
}

export function transformCompareDownloadArray(compareItems, dataArray, childKey) {
  const numberOfCompareItems = size(compareItems)
  const numberOfDataItems = size(dataArray)
  const largestDataRow = maxBy(dataArray, (item) => size(get(item, childKey, item)))
  const maxNumberOfDataRows = size(get(largestDataRow, childKey, largestDataRow))

  let newDataArray = dataArray
  if (numberOfCompareItems > numberOfDataItems) {
    let count = 0
    newDataArray = map(compareItems, (item) => {
      if (get(item, "isUnmodeled")) {
        return null
      }
      const dataItem = get(dataArray, count, {})
      count++
      return dataItem
    })
  }

  const result = Array.from({ length: maxNumberOfDataRows }, () => [])

  for (let comparisonItemValueIndex = 0; comparisonItemValueIndex < maxNumberOfDataRows; comparisonItemValueIndex++) {
    for (let newRowIndex = 0; newRowIndex < numberOfCompareItems; newRowIndex++) {
      const path = childKey ? [newRowIndex, childKey, comparisonItemValueIndex] : [newRowIndex, comparisonItemValueIndex]
      const item = get(newDataArray, path)
      if (item) {
        const { label, value } = item
        result[comparisonItemValueIndex].push(label, value)
      } else {
        result[comparisonItemValueIndex].push(null, null)
      }
    }
  }
  return result
}

export function computeSummarySheetDownload(data, compareItems, summary) {
  const summaryHeaders = ["Metric", ...map(compareItems, "label")]
  const summaryData = filter(map(summary, row => row.items || row), item => !Array.isArray(first(item)))
  const summaryRows = reduce(summaryData, (summaryResults, section) => (compact([
    ...summaryResults,
    // Composition table rows do not have parent labels, but otherwise
    // are the same format as the other comparison table rows
    ...map(section, sectionRow => slice(makeComparisonRow(sectionRow, compareItems, data, null, true), 1)),
  ])), [])
  return {
    rows: [summaryHeaders, ...summaryRows],
    name: "Summary",
  }
}

export function computeGroupSheetDownload(data, compareItems, selectedGroup) {
  const groupHeaders = compact(flatten(map(compareItems, ({ label }) => ["Category", label])))
  const groupRows = transformCompareDownloadArray(compareItems, data, "groups")
  const groupSheetName = get(selectedGroup, "classificationId")
    ? `${selectedGroup.classificationId} - ${selectedGroup.label}`
    : selectedGroup.label
  return {
    rows: [groupHeaders, ...groupRows],
    name: replace(groupSheetName, "/", "-"),
  }
}

export const POSITIONS_DISPLAY_TYPES = [{
  label: "Composition",
  options: [{
    id: "all",
    value: "all",
    label: "All Positions",
  }, {
    id: "top10",
    value: "top10",
    label: "Top 10 Positions",
  }],
}]

export const PERCENT_METRICS = ["modelCoverage", "maxConcentration", "minConcentration", "long", "short", "net"]
